import React from "react";
import { colors } from "../../../../../shared/constants/colors";
import {
  DocWriteBackStatus,
  EVWriteBackStatus,
} from "../../../../../shared/enums/evBvWriteBackStatus";
import CommentIcon from "../../../../../shared/svg/comment";
import FileIcon from "../../../../../shared/svg/fileIcon";
import { MedicalPaOrdersAuthStatus } from "../../../../enums/medicalPaOrdersAuthStatus";

/** Discriminator for which modal the external RPA status cell should open. */
export type RpaStatusForExternalModalType = "image" | "doc";

/** Props for the external worklist's RPA status cell component. */
interface RpaStatusForExternalCellProps {
  value: {
    evWriteBackStatus: string;
    documentUploadStatus: string;
    id: string;
    masterAuthStatus: string;
  };
  onRpaStatusForExternalClick?: (
    modalType: RpaStatusForExternalModalType,
    orderId: string,
  ) => void;
}

/**
 * Determines icon color based on the write-back status.
 *
 * @param status - EV or document write-back status string
 * @returns Hex color for the icon
 */
const determineColorBasedOnStatus = (status: string) => {
  switch (status) {
    case DocWriteBackStatus.SUCCESS:
    case EVWriteBackStatus.SUCCESS:
      return colors.primaryGreen[4];
    default:
      return colors.primaryGray[10];
  }
};

const MODAL_ELIGIBLE_STATUSES = [
  MedicalPaOrdersAuthStatus.Pending,
  MedicalPaOrdersAuthStatus.AuthRequired,
  MedicalPaOrdersAuthStatus.Query,
  MedicalPaOrdersAuthStatus.Hold,
];

/**
 * Whether the EV write-back icon should be visible for a given row.
 *
 * @param evWriteBackStatus - Current EV write-back status
 * @param masterAuthStatus - Master auth status of the order
 * @returns True if the icon should render
 */
const shouldShowEvWriteBackIcon = (
  evWriteBackStatus: string,
  masterAuthStatus: string,
): boolean =>
  evWriteBackStatus === EVWriteBackStatus.SUCCESS ||
  MODAL_ELIGIBLE_STATUSES.includes(
    masterAuthStatus as MedicalPaOrdersAuthStatus,
  );

/**
 * Whether the document-upload icon should be visible for a given row.
 *
 * @param documentUploadStatus - Current document upload status
 * @param masterAuthStatus - Master auth status of the order
 * @returns True if the icon should render
 */
const shouldShowDocUploadIcon = (
  documentUploadStatus: string,
  masterAuthStatus: string,
): boolean =>
  documentUploadStatus === DocWriteBackStatus.SUCCESS ||
  MODAL_ELIGIBLE_STATUSES.includes(
    masterAuthStatus as MedicalPaOrdersAuthStatus,
  );

/**
 * Thin cell component for the external worklist's RPA status column.
 * Renders EV write-back and document-upload icons and delegates all
 * business logic (Firestore fetch, modal dispatch) to the parent via
 * the `onRpaStatusForExternalClick` callback.
 *
 * @param value - Row data containing write-back statuses and order ID
 * @param onRpaStatusForExternalClick - Callback emitting the modal type and order ID on click
 */
export const RpaStatusForExternalCell: React.FC<
  RpaStatusForExternalCellProps
> = ({ value, onRpaStatusForExternalClick }) => {
  const showEvIcon = shouldShowEvWriteBackIcon(
    value.evWriteBackStatus,
    value.masterAuthStatus,
  );

  const showDocIcon = shouldShowDocUploadIcon(
    value.documentUploadStatus,
    value.masterAuthStatus,
  );

  return (
    <div className="table-cell-rpa-status ml-auto flex h-fit w-fit items-center justify-center gap-2">
      {showEvIcon && (
        <div
          onClick={(e) => {
            e.stopPropagation();
            onRpaStatusForExternalClick?.("image", value.id);
          }}
        >
          <CommentIcon
            color={determineColorBasedOnStatus(value.evWriteBackStatus)}
            height={"24px"}
            width={"24px"}
          />
        </div>
      )}
      {showDocIcon && (
        <div
          onClick={(e) => {
            e.stopPropagation();
            onRpaStatusForExternalClick?.("doc", value.id);
          }}
        >
          <FileIcon
            strokeColor={determineColorBasedOnStatus(
              value.documentUploadStatus,
            )}
            height={20}
            width={20}
          />
        </div>
      )}
    </div>
  );
};

export default RpaStatusForExternalCell;
